import { AlertTriangle, Bug, Home, RefreshCw } from 'lucide-react'
import React from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface ErrorFallbackProps {
  error?: Error | null
  resetError?: () => void
  showDetails?: boolean
}

export default function ErrorFallback({
  error,
  resetError,
  showDetails = false,
}: ErrorFallbackProps) {
  const [showErrorDetails, setShowErrorDetails] = React.useState(false)

  const handleRefresh = () => {
    if (resetError) {
      resetError()
    } else {
      window.location.reload()
    }
  }

  const handleGoHome = () => {
    window.location.href = '/'
  }

  const isNetworkError =
    error?.message &&
    (error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('Failed to fetch'))

  const is401Error =
    error?.message &&
    (error.message.includes('401') || error.message.includes('Unauthorized'))

  const getErrorTitle = () => {
    if (is401Error) return 'Session Expired'
    if (isNetworkError) return 'Connection Problem'
    return 'Something went wrong'
  }

  const getErrorDescription = () => {
    if (is401Error) {
      return 'Your session has expired. Please refresh the page to log in again.'
    }
    if (isNetworkError) {
      return 'Unable to connect to the server. Please check your internet connection and try again.'
    }
    return 'An unexpected error occurred. Our team has been notified and is working to fix this issue.'
  }

  const getActionButtons = () => {
    if (is401Error) {
      return (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={handleRefresh} className="w-full sm:w-auto">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>
        </div>
      )
    }

    return (
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button onClick={handleRefresh} className="w-full sm:w-auto">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
        <Button
          variant="outline"
          onClick={handleGoHome}
          className="w-full sm:w-auto"
        >
          <Home className="mr-2 h-4 w-4" />
          Go to Home
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-md">
        <Card className="border-red-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              {getErrorTitle()}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {getErrorDescription()}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Error Alert */}
            <Alert className="border-red-200 bg-red-50">
              <Bug className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                Error ID: {Date.now().toString(36)}
              </AlertDescription>
            </Alert>

            {/* Action Buttons */}
            {getActionButtons()}

            {/* Error Details Toggle (for development) */}
            {showDetails && error && (
              <div className="border-t border-gray-200 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowErrorDetails(!showErrorDetails)}
                  className="w-full text-xs text-gray-500 hover:text-gray-700"
                >
                  {showErrorDetails ? 'Hide' : 'Show'} Error Details
                </Button>

                {showErrorDetails && (
                  <div className="mt-2 p-3 bg-gray-100 rounded-md border">
                    <div className="text-xs font-mono text-gray-700 break-all">
                      <div className="font-semibold mb-1">Error Message:</div>
                      <div className="mb-2">{error.message}</div>

                      {error.stack && (
                        <>
                          <div className="font-semibold mb-1">Stack Trace:</div>
                          <pre className="whitespace-pre-wrap text-xs">
                            {error.stack}
                          </pre>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Help Text */}
            <div className="text-center text-xs text-gray-500 border-t border-gray-200 pt-4">
              If this problem persists, please contact your system
              administrator.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
